import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { HealthReportData } from '@/services/healthReportGenerator';
import { useI18n } from '@/i18n/LanguageContext';
import { useSettings } from '@/contexts/SettingsContext';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { Car as CarIcon } from 'lucide-react';

interface HealthReportViewProps {
  data: HealthReportData;
}

const HealthReportView: React.FC<HealthReportViewProps> = ({ data }) => {
  const { t, formatNumber } = useI18n();
  const { settings } = useSettings();

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatMileage = (km: number | null | undefined) => {
    if (km === null || km === undefined) return '-';
    if (settings?.distanceUnit === 'mi') {
      return `${formatNumber(Math.round(km / 1.609344))} mi`;
    }
    return `${formatNumber(km)} km`;
  };

  const getFluidTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      engine_oil: t('fluids.engineOil') !== 'fluids.engineOil' ? t('fluids.engineOil') : 'Engine Oil',
      brake_fluid: t('fluids.brakeFluid') !== 'fluids.brakeFluid' ? t('fluids.brakeFluid') : 'Brake Fluid',
      coolant: t('fluids.coolant') !== 'fluids.coolant' ? t('fluids.coolant') : 'Coolant',
      washer_fluid: t('fluids.washerFluid') !== 'fluids.washerFluid' ? t('fluids.washerFluid') : 'Washer Fluid',
    };
    return labels[type] || type;
  };

  const getFuelTypeLabel = (fuelType?: string) => {
    if (!fuelType) return '';
    const labels: Record<string, string> = {
      gasoline: t('car.fuelType.gasoline') || 'Gasoline',
      diesel: t('car.fuelType.diesel') || 'Diesel',
      electric: t('car.fuelType.electric') || 'Electric',
      hybrid: t('car.fuelType.hybrid') || 'Hybrid',
      lpg: t('car.fuelType.lpg') || 'LPG',
      cng: t('car.fuelType.cng') || 'CNG',
    };
    return labels[fuelType.toLowerCase()] || fuelType;
  };

  const getTransmissionLabel = (transmission?: string) => {
    if (!transmission) return '';
    const labels: Record<string, string> = {
      manual: t('car.transmission.manual') || 'Manual',
      automatic: t('car.transmission.automatic') || 'Automatic',
    };
    return labels[transmission.toLowerCase()] || transmission;
  };

  const ownerName = data.owner.firstName || data.owner.lastName
    ? `${data.owner.firstName || ''} ${data.owner.lastName || ''}`.trim()
    : data.owner.displayName || '';

  const [carImageUrl, setCarImageUrl] = useState<string | null>(null);
  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);

  // Load car image
  useEffect(() => {
    const loadCarImage = async () => {
      if (data.car.imagePath) {
        try {
          const signedUrlInfo = await getSignedViewUrl(data.car.imagePath);
          setCarImageUrl(signedUrlInfo.url);
        } catch (error) {
          console.error('Error loading car image:', error);
          setCarImageUrl(null);
        }
      }
    };
    loadCarImage();
  }, [data.car.imagePath]);

  // Owner avatar is already a URL (from generator), just use it
  useEffect(() => {
    if (data.owner.avatarUrl) {
      setOwnerAvatarUrl(data.owner.avatarUrl);
    }
  }, [data.owner.avatarUrl]);

  return (
    <>
      <style>{`
        /* CSS rules for PDF generation - html2pdf.js/html2canvas respects these */
        .pdf-avoid-break {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .pdf-section {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        /* Override background color for PDF generation */
        .pdf-only-white {
          background-color: white !important;
        }
        /* Also include @media print for browser print compatibility */
        @media print {
          .pdf-avoid-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .pdf-section {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .pdf-only-white {
            background-color: white !important;
          }
        }
      `}</style>
      <div className="bg-[#F5F5F5] font-lato text-black p-4" id="health-report-content">
      {/* Header */}
      <header className="text-center mb-6">
        <h1 className="text-2xl font-bold">{t('healthReport.title') || 'Car Health Report'}</h1>
        <p className="text-xs text-gray-500 mt-1">
          {t('healthReport.generatedAt') || 'Generated'}: {formatDateTime(data.generatedAt)}
        </p>
      </header>

      {/* Car Information */}
      <section className="bg-white rounded-xl shadow-md p-4 mb-6 pdf-avoid-break">
        <div className="flex items-center gap-3 mb-3">
          {carImageUrl ? (
            <img
              src={carImageUrl}
              alt={`${data.car.make} ${data.car.model}`}
              className="w-14 h-14 rounded-lg object-cover"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={() => setCarImageUrl(null)}
            />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center">
              <CarIcon className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div>
            {/* Make / Model / Year as title */}
            <h2 className="text-base font-semibold">
              {data.car.make} {data.car.model} · {data.car.year}
            </h2>
            {/* Plate just below */}
            {data.car.licensePlate && (
              <p className="text-sm text-gray-500">Plate: {data.car.licensePlate}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs leading-tight">
          {data.car.fuelType && (
            <p>
              <span className="text-gray-500">
                {t('healthReport.fuelLabel') || 'Fuel'}:
              </span>{' '}
              {getFuelTypeLabel(data.car.fuelType)}
            </p>
          )}
          {data.car.transmission && (
            <p>
              <span className="text-gray-500">
                {t('healthReport.transmissionLabel') || 'Transmission'}:
              </span>{' '}
              {getTransmissionLabel(data.car.transmission)}
            </p>
          )}
          {data.car.vin && (
            <p>
              <span className="text-gray-500">VIN:</span> {data.car.vin}
            </p>
          )}
          {(data.currentMileage !== null || data.lastMileageDate) && (
            <p>
              <span className="text-gray-500">
                {t('healthReport.lastMileageLabel') || 'Last Mileage'}:
              </span>{' '}
              {data.currentMileage !== null ? formatMileage(data.currentMileage) : '-'}
              {data.lastMileageDate && (
                <span className="text-gray-500"> ({formatDate(data.lastMileageDate)})</span>
              )}
            </p>
          )}
        </div>
        {(ownerName || data.owner.phoneNumber) && (
          <>
            <hr className="my-3 border-gray-200" />
            <div className="flex items-center gap-3 text-xs leading-tight">
              {ownerAvatarUrl ? (
                <img
                  src={ownerAvatarUrl}
                  alt={ownerName || 'Owner'}
                  className="w-10 h-10 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onError={() => setOwnerAvatarUrl(null)}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300" />
              )}
              <div>
                <p className="font-semibold mb-1">
                  {t('healthReport.contactLabel') || 'Contact'}
                </p>
                {ownerName && <p>{ownerName}</p>}
                {data.owner.phoneNumber && <p>{data.owner.phoneNumber}</p>}
              </div>
            </div>
          </>
        )}
      </section>

      {/* Active Issues */}
      <section className="mb-6 pdf-section">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          {t('healthReport.sections.activeIssues') || 'Active Issues'}
          <span className="text-sm font-normal text-gray-500">({data.activeIssues.length})</span>
        </h2>
        {data.activeIssues.length === 0 ? (
          <p className="text-gray-500 italic text-sm">
            {t('healthReport.noActiveIssues') || 'No active issues'}
          </p>
        ) : (
          <div className="space-y-3">
            {data.activeIssues.map((issue, index) => (
              <div key={issue.id} className="bg-white rounded-xl shadow-sm p-4 pdf-avoid-break">
                <div className="flex items-start gap-2">
                  <div 
                    className="flex items-center justify-center rounded-full font-semibold text-sm min-w-[24px] h-6 px-2"
                    style={{ backgroundColor: '#eff2fc' }}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold" style={{ fontSize: '0.875rem' }}>{issue.title || issue.description.substring(0, 50)}</h3>
                    <p className="text-gray-600 mt-1" style={{ fontSize: '0.8rem' }}>{issue.aiSummary || issue.description}</p>
                    <p className="text-gray-400 mt-1" style={{ fontSize: '0.8rem' }}>
                      {t('healthReport.reported') || 'Reported'}: {formatDate(issue.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pending Tasks */}
      <section className="mb-6 pdf-section">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          {t('healthReport.sections.pendingTasks') || 'Pending Tasks'}
          <span className="text-sm font-normal text-gray-500">({data.pendingTasks.length})</span>
        </h2>
        {data.pendingTasks.length === 0 ? (
          <p className="text-gray-500 italic text-sm">
            {t('healthReport.noPendingTasks') || 'No pending tasks'}
          </p>
        ) : (
          <div className="space-y-3">
            {data.pendingTasks.map((task, index) => (
              <div key={`${task.id}-${index}`} className="bg-white rounded-xl shadow-sm p-4 pdf-avoid-break">
                <div className="flex items-start gap-2">
                  <div 
                    className="flex items-center justify-center rounded-full font-semibold text-sm min-w-[24px] h-6 px-2"
                    style={{ backgroundColor: '#eff2fc' }}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-sm">{task.title}</h3>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-1">{task.description}</p>
                    )}
                    <div className="flex justify-between items-center mt-2 text-xs text-gray-400">
                      <span>
                        {t('healthReport.dateCreated') || 'Date created'}: {task.createdAt ? formatDate(task.createdAt) : '-'}
                      </span>
                      {(task.dueDate || task.dueOdometer) && (
                        <span>
                          {t('healthReport.due') || 'Due'}:{' '}
                          {task.dueDate && formatDate(task.dueDate)}
                          {task.dueDate && task.dueOdometer && ' · '}
                          {task.dueOdometer && formatMileage(task.dueOdometer)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Fluid Change History */}
      <section className="mb-6 pdf-section">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          {t('healthReport.sections.fluidHistory') || 'Fluid Change History'}
          <span className="text-sm font-normal text-gray-500">({data.fluidHistory.length})</span>
        </h2>
        {data.fluidHistory.length === 0 ? (
          <p className="text-gray-500 italic text-sm">
            {t('healthReport.noFluidHistory') || 'No fluid history recorded'}
          </p>
        ) : (
          <div className="space-y-3">
            {data.fluidHistory.map((fluid, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm p-4 text-xs leading-tight pdf-avoid-break">
                <h3 className="font-semibold mb-1">{getFluidTypeLabel(fluid.fluid_type)}</h3>
                {fluid.latest_spec_code && (
                  <p>
                    {t('healthReport.specCode') || 'Spec'}: {fluid.latest_spec_code}
                  </p>
                )}
                {fluid.latest_color && (
                  <p>
                    {t('healthReport.color') || 'Color'}: {fluid.latest_color}
                  </p>
                )}
                {fluid.last_refill_date && (
                  <p>
                    {(t('healthReport.lastRefill') || 'Last refill') + ': '}{formatDate(fluid.last_refill_date)}
                    {fluid.last_refill_odometer && ` · ${formatMileage(fluid.last_refill_odometer)}`}
                  </p>
                )}
                {fluid.last_replacement_date && (
                  <p>
                    {(t('healthReport.lastReplacement') || 'Last replacement') + ': '}{formatDate(fluid.last_replacement_date)}
                    {fluid.last_replacement_odometer && ` · ${formatMileage(fluid.last_replacement_odometer)}`}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Service History */}
      <section className="mb-6 pdf-section">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          {t('healthReport.sections.serviceHistory') || 'Service History'}
          <span className="text-sm font-normal text-gray-500">({data.serviceHistory.length})</span>
        </h2>
        {data.serviceHistory.length === 0 ? (
          <p className="text-gray-500 italic text-sm">
            {t('healthReport.noServiceHistory') || 'No service history recorded'}
          </p>
        ) : (
          <div className="space-y-3">
            {data.serviceHistory.map((entry, index) => (
              <div key={entry.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 pdf-avoid-break">
                <div className="relative">
                  <div className="flex items-start gap-2 mb-2">
                    <div 
                      className="flex items-center justify-center rounded-full font-semibold text-sm min-w-[24px] h-6 px-2"
                      style={{ backgroundColor: '#eff2fc' }}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 flex items-start justify-between gap-2 relative">
                      <div className="flex items-start gap-2">
                        <p className="font-semibold">{formatDate(entry.serviceDate)}</p>
                      </div>
                      {(entry.shopName || entry.mechanicName) && (
                        <>
                          <div className="absolute left-1/2 top-0 bottom-[-12px] -translate-x-1/2 border-l border-gray-200"></div>
                          <div className="flex flex-col items-end" style={{ fontSize: '0.8rem' }}>
                            {entry.shopName && (
                              <p className="text-gray-700 text-right">{entry.shopName}</p>
                            )}
                            {entry.mechanicName && (
                              <p className="text-gray-700 text-right">{entry.mechanicName}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <hr className="my-3 border-gray-200" />
                </div>
                {entry.workDone && entry.workDone.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-semibold mb-1">
                      {t('healthReport.workDone') || 'Work done'}:
                    </p>
                    <ul className="text-sm pl-4" style={{ fontSize: '0.8rem' }}>
                      {entry.workDone.map((work, idx) => (
                        <li key={idx} className="mb-1">
                          <p className="font-medium text-gray-700">{idx + 1}. {work.title}</p>
                          {work.notes && (
                            <p className="text-xs text-gray-500 pl-6">{work.notes}</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(entry.findings || entry.recommendations) && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-semibold mb-2">
                      {t('healthReport.findingsAndRecommendations') || 'Findings & recommendations'}:
                    </p>
                    {entry.findings && (
                      <p className="text-gray-700 mb-2" style={{ fontSize: '0.8rem' }}>{entry.findings}</p>
                    )}
                    {entry.recommendations && (
                      <p className="text-gray-700" style={{ fontSize: '0.8rem' }}>{entry.recommendations}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="text-center mt-10 mb-6 text-xs text-gray-400">
        {t('healthReport.footer') || 'Generated by '}
        <a 
          href="https://onwheels.live" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-soft-blue hover:underline"
        >
          On Wheels
        </a>
      </footer>
      </div>
    </>
  );
};

export default HealthReportView;

